import {
  RecommendationAction,
  RecommendationActionEntry,
  RecommendationActionsSnapshot,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import {
  RecommendationRowRef,
  resolveAutoAcceptedActions,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/auto-accepted-actions';
import { serializeActionEntry } from '@/experiences/sales-portal/domain/pricing-agent/blocs/utils/count-actions';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useRecommendationActionsStore } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';
import { useUpsertRecommendationStatusMutator } from '@/experiences/sales-portal/domain/pricing-agent/blocs/mutators/use-upsert-recommendation-status-mutator';

const SYNC_DEBOUNCE_MS = 800;

const NO_ACTIONS: RecommendationActionsSnapshot = {};

// Auto-accepted rows are already accepted on the backend, so a row we have
// never synced is assumed to be in this state and only divergences are sent.
const DEFAULT_ACCEPTED_SIGNATURE = serializeActionEntry({
  action: RecommendationAction.Accepted,
  recommendationUUID: '',
  supplierUUID: '',
});

const collectChangedEntries = (
  effectiveActions: RecommendationActionsSnapshot,
  lastSynced: Map<string, string>,
  baseline: Map<string, string>,
): RecommendationActionEntry[] =>
  Object.values(effectiveActions).filter(
    (entry) =>
      (lastSynced.get(entry.recommendationUUID) ??
        baseline.get(entry.recommendationUUID) ??
        DEFAULT_ACCEPTED_SIGNATURE) !== serializeActionEntry(entry),
  );

interface Params {
  enabled: boolean;
  rows: RecommendationRowRef[];
  taskTypeUUID: string;
}

export interface AutoPublishStatusSync {
  /**
   * Sends any debounced edit immediately and waits for in-flight sends,
   * resolving to whether every edit is now persisted. Publish has to await this:
   * rejections travel through this sync rather than the publish payload, so an
   * unsynced rejection would otherwise be dropped by both.
   */
  flushPendingSync: () => Promise<boolean>;
}

/**
 * For auto-accepted tasks, eagerly persists each row's effective status to the
 * backend (debounced), sending only what diverges from what the backend already
 * has. Because the effective status covers untouched rows too, un-rejecting a
 * row syncs it back to accepted without needing to special-case a reset.
 */
export const useAutoPublishStatusSync = ({
  enabled,
  rows,
  taskTypeUUID,
}: Params): AutoPublishStatusSync => {
  const actions = useRecommendationActionsStore((state) => state.actions);
  const actionsResetEpoch = useRecommendationActionsStore(
    (state) => state.actionsResetEpoch,
  );
  const markActionsSynced = useRecommendationActionsStore(
    (state) => state.markActionsSynced,
  );
  const { upsertActions } = useUpsertRecommendationStatusMutator();

  const effectiveActions = useMemo(
    () => (enabled ? resolveAutoAcceptedActions(rows, actions) : NO_ACTIONS),
    [actions, enabled, rows],
  );
  const effectiveActionsRef = useRef(effectiveActions);

  // Each row's current backend status — the comparison point, so a row already
  // at its backend status (e.g. after a publish + refetch) is never re-sent.
  const baselineSignatures = useMemo(
    () =>
      new Map(
        Object.values(resolveAutoAcceptedActions(rows, NO_ACTIONS)).map(
          (entry) => [entry.recommendationUUID, serializeActionEntry(entry)],
        ),
      ),
    [rows],
  );
  const baselineSignaturesRef = useRef(baselineSignatures);

  // recommendation UUID -> serialized status last persisted to the backend.
  const lastSyncedRef = useRef<Map<string, string>>(new Map());

  // A programmatic wipe (publish, stale prune, task switch) is not the user
  // resetting rows, so drop the baseline instead of syncing the wipe back.
  useEffect(() => {
    lastSyncedRef.current = new Map();
  }, [actionsResetEpoch, taskTypeUUID]);

  const syncNow = useCallback(async () => {
    const lastSynced = lastSyncedRef.current;
    const changed = collectChangedEntries(
      effectiveActionsRef.current,
      lastSynced,
      baselineSignaturesRef.current,
    );

    if (changed.length === 0) {
      return;
    }

    const succeeded = await upsertActions(changed);
    if (!succeeded) {
      return;
    }

    for (const entry of changed) {
      lastSynced.set(entry.recommendationUUID, serializeActionEntry(entry));
    }
    // The backend now owns these decisions, so the task card must stop counting
    // them on top of its published baseline.
    markActionsSynced(changed);
  }, [markActionsSynced, upsertActions]);

  // Run the syncs one at a time: each one sees the baseline the previous one
  // committed and the latest edits, so an in-flight request can never leave a
  // later edit stranded as an empty diff.
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const sync = useDebouncedCallback(() => {
    queueRef.current = queueRef.current.then(syncNow).catch(() => {});
  }, SYNC_DEBOUNCE_MS);

  useEffect(() => {
    effectiveActionsRef.current = effectiveActions;
    baselineSignaturesRef.current = baselineSignatures;
    if (!enabled) {
      return;
    }
    sync();
  }, [baselineSignatures, effectiveActions, enabled, sync]);

  // Flush any pending edit when leaving the screen so it isn't lost.
  useEffect(() => () => void sync.flush(), [sync]);

  const hasUnsyncedChanges = useCallback(
    () =>
      collectChangedEntries(
        effectiveActionsRef.current,
        lastSyncedRef.current,
        baselineSignaturesRef.current,
      ).length > 0,
    [],
  );

  const flushPendingSync = useCallback(async () => {
    sync.flush();
    await queueRef.current;

    // A failed send leaves nothing debounced, so the flush above has nothing to
    // run and the edit would stay unsent forever. Retry it here — through the
    // same queue, so the retry can never race a newer edit.
    if (hasUnsyncedChanges()) {
      queueRef.current = queueRef.current.then(syncNow).catch(() => {});
      await queueRef.current;
    }

    return !hasUnsyncedChanges();
  }, [hasUnsyncedChanges, sync, syncNow]);

  return useMemo(() => ({ flushPendingSync }), [flushPendingSync]);
};
